import styled, { keyframes } from 'styled-components';
import { compose } from 'styled-system';

import {
  COMMON,
  LAYOUT,
  SystemCommonProps,
  SystemLayoutProps
} from '../theme/constants';
import { pxToRem } from '../theme/utils/pxToRem';
import sx, { SxProp } from '../theme/utils/sx';

const composeSystem = compose(COMMON, LAYOUT);

export interface StyledCheckboxProps
  extends SxProp,
    SystemCommonProps,
    SystemLayoutProps {
  colorSchema?: 'active' | 'success' | 'warning' | 'error';
  rounded?: boolean;
}

export const CheckboxRoot = styled.span`
  display: inline-block;
  height: ${pxToRem(24)};
  width: ${pxToRem(24)};
  position: relative;

  ${composeSystem}
  ${sx}
`;

export const InputCheckbox = styled.input`
  opacity: 0;
  position: absolute;
  width: 100%;
  height: 100%;
  margin: auto;
  position: absolute;
  cursor: pointer;
  z-index: 100;

  &:disabled,
  &[readonly] {
    cursor: auto;
    pointer-events: none;
  }
`;

export const CheckIcon = styled.svg`
  color: #fff;
  height: 66.66%;
  width: 100%;
  margin: auto;
`;

const uncheckCheckmarkPath = keyframes`
  from {
    animation-timing-function: cubic-bezier(0.4, 0, 0.6, 1);
    opacity: 1;
    stroke-dashoffset: 0;
  }

  to {
    opacity: 0;
    stroke-dashoffset: -29.7833385;
  }
`;

export const CheckIconPath = styled.path`
  stroke: currentColor;
  stroke-width: 3.12px;
  stroke-dashoffset: 29.7833385;
  stroke-dasharray: 29.7833385;
  stroke-linecap: round;
  stroke-linejoin: round;
  transition: stroke-dashoffset 0.18s cubic-bezier(0.4, 0, 0.6, 1) 90ms;
  transition: none;

  // Uncheck animation
  animation-name: ${uncheckCheckmarkPath};
  animation-duration: 180ms;
  animation-delay: 0ms;
`;

export const CheckIconContainer = styled.span<StyledCheckboxProps>`
  color: #fff;
  position: absolute;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  border-radius: ${({ rounded }) => (rounded ? '50%' : '16.66%')};
  padding: ${({ rounded }) => (rounded ? '1px' : 0)};
  background: transparent;
  border: solid 1px ${({ theme }) => theme.colors.grey[50]};
  display: flex;
  transition: background-color 90ms cubic-bezier(0.4, 0, 0.6, 1) 140ms,
    border-color 90ms cubic-bezier(0.4, 0, 0.6, 1) 140ms, transform 40ms ease-in;
  z-index: 1;

  ${InputCheckbox}:active ~ & {
    transform: scale(0.9);
  }
  ${InputCheckbox}[readonly] ~ & {
    transform: none;
  }

  ${InputCheckbox}:checked ~ & {
    background: ${({ colorSchema, theme }) => theme.colors[colorSchema].base};
    border-color: ${({ colorSchema, theme }) => theme.colors[colorSchema].base};
    border-width: 0;
    transition: background-color 90ms cubic-bezier(0.4, 0, 0.6, 1) 0ms,
      border-color 90ms cubic-bezier(0.4, 0, 0.6, 1) 0ms, transform 40ms ease-in;

    & ${CheckIcon} ${CheckIconPath} {
      transition: stroke-dashoffset 0.18s cubic-bezier(0.4, 0, 0.6, 1) 90ms;
      stroke-dashoffset: 0;
      animation: none;
    }
  }

  ${InputCheckbox}:checked:disabled ~ & {
    background: ${({ theme }) => theme.colors.grey[50]};
    border-color: ${({ theme }) => theme.colors.grey[50]};

    & ${CheckIcon} ${CheckIconPath} {
      stroke: ${({ theme }) => theme.colors.grey[10]};
    }
  }

  ${InputCheckbox}:disabled ~ & {
    background: ${({ theme }) => theme.colors.grey[10]};
    border-color: ${({ theme }) => theme.colors.grey[50]};
  }
`;

export const CheckboxRipple = styled.span<StyledCheckboxProps>`
  width: 166%;
  height: 166%;
  margin: auto;
  left: 50%;
  bottom: 0;
  top: 0;
  transform: translateX(-50%);
  z-index: 0;
  position: absolute;
  background-color: ${({ theme }) => theme.colors.grey[50]};
  opacity: 0.1;
  border-radius: 50%;
  opacity: 0;
  transition: opacity 15ms linear, background-color 15ms linear;

  ${InputCheckbox}:hover ~ & {
    opacity: 0.1;
  }
  ${InputCheckbox}:focus ~ & {
    opacity: 0.1;
  }
  ${InputCheckbox}[readonly] ~ & {
    opacity: 0;
  }
  ${InputCheckbox}:checked ~ & {
    background-color: ${({ colorSchema, theme }) =>
      theme.colors[colorSchema].base};
  }
  ${InputCheckbox}:disabled ~ & {
    display: none;
  }
`;
